import type { Logger } from '@vp/ubik-logging'

import type { Project } from '../../../types/Project'
import type { NewUdsDocument } from '../../../types/UdsDocument'
import type { PageMode } from '../../../types/QuantitySelectionData'
import type { WorkId } from '../../../types/WorkId'
import { createAndSaveWork } from '../../../utils/WorkHelpers'
import type { MerchantAvailabilityResponse } from '../../../types/MerchantAvailability'
import { getInventoryCount } from '../../../utils/MerchantAvailabilityHelpers'
import type { ColorOptionProduct } from '../../../types/ColorOptionProduct'
import { postDocument } from '../../../clients/UdsClient'
import { REQUESTOR } from '../../../utils/constants'
import type { MsxV2PropertyValue } from '../../../types/MsxProperty'

const artworkGenerationUrl = 'https://artworkgeneration.cimpress.io/api/v3'
const ARTWORK_GENERATION_PUBLIC_API_KEY = '5wmXA2wOooZR59Pw1iU8JREI5IkBqzrG5I3'

export function buildDocUrlForFontColorAdjustedTemplate(documentUrl: string, hexColorValue: string, requestor: string): string {
  const params = {
    documentUrl,
    requester: requestor,
    targetSubstrateColor: `rgb(${hexColorValue})`,
    minimumContrastRatio: 3,
    apiKey: ARTWORK_GENERATION_PUBLIC_API_KEY,
  }

  return `${artworkGenerationUrl}/adaptation:adjustForSubstrate?${buildStringFromParams(params)}`
}

export function buildStringFromParams(params: { [key: string]: string | number }): string {
  return Object.entries(params)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&')
}

export const getColorKey = (lowestPriceAttributes?: Record<string, string>) => {
  return lowestPriceAttributes?.['Color'] ? 'Color' : 'Substrate Color'
}

export const createWorkFromColorOption = async (
  cartTenant: string,
  mpvTenant: string,
  culture: string,
  ownerId: string,
  requestor: string,
  isCareAgent: boolean,
  editUrlPath: string,
  primaryProjectPageMode: PageMode,
  primaryProject: Project,
  hexColorValue: string,
  colorKey: string,
  authorizationHeader: string,
  logger: Logger,
  existingWorkIdForSelectedColorOption?: WorkId,
): Promise<WorkId> => {
  if (existingWorkIdForSelectedColorOption) {
    return existingWorkIdForSelectedColorOption // this color option already has an associated work entity
  }
  const newDoc = buildDocUrlForFontColorAdjustedTemplate(primaryProject.work.design.designUrl, hexColorValue, requestor)

  const response = await fetch(newDoc)
  if (!response.ok) {
    throw new Error(`Failed to fetch adjusted document: ${response.status}`)
  }
  const cimDoc = (await response.json()) as NewUdsDocument

  // we need to store the transformed document to UDS.
  const postDocResponse = await postDocument(cimDoc, authorizationHeader, logger)

  // logic assumes colors use the same page mode as the primary project
  const isSizedGood = primaryProjectPageMode === 'Sized Good'

  return await createAndSaveWork(
    cartTenant,
    primaryProject.productName,
    editUrlPath,
    postDocResponse,
    ownerId,
    primaryProject.productKey,
    primaryProject.productVersion,
    mpvTenant,
    culture,
    primaryProject.mpvId,
    {
      ...primaryProject.work.merchandising.merchandisingSelections,
      [colorKey]: hexColorValue,
    },
    primaryProject.minimumOrderQuantity,
    isSizedGood,
    isCareAgent,
    authorizationHeader,
    logger,
  )
}

export const generatePreviewInstructionsUriForColor = (primaryProject: Project | undefined, colorOption: string): string => {
  if (primaryProject && primaryProject.work) {
    const documentUrl = buildDocUrlForFontColorAdjustedTemplate(primaryProject.work.design.designUrl, colorOption, REQUESTOR)

    const previewInstructionsUri = `https://instructions.documents.cimpress.io/v3/instructions:preview?documentUri=${encodeURIComponent(documentUrl)}`
    return previewInstructionsUri
  } else {
    return ''
  }
}

export const getAvailableColorsFromInventory = (inventoryByColor: MerchantAvailabilityResponse | undefined, colorKey: string, colorValue: string): string[] => {
  const availableColors: string[] = []
  if (inventoryByColor) {
    inventoryByColor.facetedAvailabilityStates.forEach((facet) => {
      if (getInventoryCount(facet.availabilityState) > 0) {
        const color = facet.attributeSelections.find((attr) => attr.name === colorKey)?.value
        if (color && color !== colorValue) availableColors.push(color)
      }
    })
  }
  return availableColors
}

export const buildColorOptionProducts = (
  availableColors: string[],
  colorNames: MsxV2PropertyValue[],
  primaryProject: Project,
  colorKey: string,
): ColorOptionProduct[] => {
  return availableColors.map((colorOption) => {
    const colorName = colorNames.find((color) => color.key === colorOption)?.merchandisingName
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { Size, ...lowestPriceAttributesWithoutSize } = primaryProject.lowestPriceAttributes

    return {
      productKey: primaryProject.productKey as string,
      productVersion: primaryProject.productVersion as number,
      selectedOptions: {
        ...lowestPriceAttributesWithoutSize,
        [colorKey]: colorOption,
      },
      hexColorValue: colorOption,
      renderingInstructionsUrl: generatePreviewInstructionsUriForColor(primaryProject, colorOption),
      productName: primaryProject.productName,
      colorName,
    }
  })
}
